import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { formatPhone } from '../services/firebase';

export default function RecordPaymentModal({ driver, onClose, onPaymentRecorded, clearDriverPendingDues }) {
  const pending = Math.max(0, Number(driver?.pendingDues) || 0);
  const [amount, setAmount] = useState(String(pending > 0 ? pending : ''));
  const [method, setMethod] = useState('cash'); // 'cash' | 'upi'
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  if (!driver) return null;

  const numAmount = Number(amount);
  const isValidAmount = Number.isFinite(numAmount) && numAmount > 0 && numAmount <= pending;
  const remaining = isValidAmount ? Math.round((pending - numAmount) * 100) / 100 : pending;

  const quickAmounts = [50, 100, 200].filter((a) => a < pending);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!isValidAmount) {
      toast.error(`Enter a valid amount between ₹1 and ₹${pending}`);
      return;
    }

    setBusy(true);
    const paymentId = window.crypto?.randomUUID ? window.crypto.randomUUID() : `pay_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    try {
      await clearDriverPendingDues({
        driverId: driver.id,
        amount: numAmount,
        paymentId,
        expectedLastPaymentId: driver.lastDuesPaymentId || null,
        expectedPendingDues: pending,
        method,
        notes: notes.trim(),
      });
      toast.success(`₹${numAmount} dues payment recorded for ${driver.name || 'Anna'}!`);
      onPaymentRecorded?.(driver.id, numAmount, remaining);
      onClose?.();
    } catch (err) {
      console.error('recordDriverPayment error:', err);
      toast.error(err?.message || 'Failed to record payment. Please check driver balance and try again.');
    } finally {
      setBusy(false);
    }
  };

  const avatar = driver.photoUrl || driver.pendingPhotoUrl || driver.driverPhoto || driver.profilePhoto;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal-sheet"
        style={{ maxWidth: 460, background: '#0f172a', border: '1px solid #334155', borderRadius: 16, padding: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#f8fafc' }}>
              💰 Collect Platform Dues
            </h3>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              Record cash or UPI commission collected from Anna
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>✕</button>
        </div>

        {/* Driver Summary Card */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12,
          padding: '12px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
        }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {avatar ? (
              <img src={avatar} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover' }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#1e293b', display: 'grid', placeItems: 'center', fontSize: 20 }}>🛺</div>
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#f8fafc' }}>{driver.name || 'Anna'}</div>
              <div className="mono muted" style={{ fontSize: 12 }}>{driver.vehicleNumber || '—'} · {formatPhone(driver.phone)}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 700 }}>Current Dues</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#f87171' }}>₹{pending}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Quick Amount Buttons */}
          <div style={{ marginBottom: 16 }}>
            <label className="field-label" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
              SELECT AMOUNT TO RECEIVE
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {quickAmounts.map((q) => (
                <button
                  key={q}
                  type="button"
                  className={`btn btn-sm ${numAmount === q ? 'btn-green' : 'btn-ghost'}`}
                  style={{ borderRadius: 8, padding: '6px 14px', fontWeight: 700 }}
                  onClick={() => setAmount(String(q))}
                  disabled={busy}
                >
                  ₹{q}
                </button>
              ))}
              <button
                type="button"
                className={`btn btn-sm ${numAmount === pending ? 'btn-green' : 'btn-ghost'}`}
                style={{ borderRadius: 8, padding: '6px 14px', fontWeight: 700 }}
                onClick={() => setAmount(String(pending))}
                disabled={busy}
              >
                Full Due (₹{pending})
              </button>
            </div>
          </div>

          {/* Custom Amount Input */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label className="field-label" style={{ fontSize: 12, margin: 0 }}>AMOUNT RECEIVED (₹)</label>
              <span className="muted" style={{ fontSize: 11 }}>Max: ₹{pending}</span>
            </div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: 10, color: '#94a3b8', fontWeight: 700 }}>₹</span>
              <input
                type="number"
                min="1"
                max={pending}
                step="1"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={busy}
                style={{
                  width: '100%',
                  paddingLeft: 28,
                  fontSize: 16,
                  fontWeight: 700,
                  background: '#1e293b',
                  border: !isValidAmount && amount ? '1px solid #ef4444' : '1px solid #334155',
                  color: '#fff',
                  borderRadius: 8,
                }}
              />
            </div>
          </div>

          {/* Payment Method Selector */}
          <div style={{ marginBottom: 16 }}>
            <label className="field-label" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
              COLLECTION METHOD
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                type="button"
                className={`btn ${method === 'cash' ? 'btn-green' : 'btn-ghost'}`}
                style={{ justifyContent: 'center', fontWeight: 700 }}
                onClick={() => setMethod('cash')}
                disabled={busy}
              >
                💵 Cash Handover
              </button>
              <button
                type="button"
                className={`btn ${method === 'upi' ? 'btn-green' : 'btn-ghost'}`}
                style={{ justifyContent: 'center', fontWeight: 700 }}
                onClick={() => setMethod('upi')}
                disabled={busy}
              >
                📱 UPI / QR Scan
              </button>
            </div>
          </div>

          {/* Notes / Reference */}
          <div style={{ marginBottom: 18 }}>
            <label className="field-label" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
              NOTE / REFERENCE (OPTIONAL)
            </label>
            <input
              type="text"
              placeholder="e.g. Bus Stand handover / PhonePe ref"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
              style={{
                width: '100%',
                fontSize: 13,
                background: '#1e293b',
                border: '1px solid #334155',
                color: '#fff',
                borderRadius: 8,
              }}
            />
          </div>

          {/* Balance Preview Card */}
          <div style={{
            background: 'rgba(15, 23, 42, 0.6)',
            border: '1px solid rgba(51, 65, 85, 0.6)',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 20,
            fontSize: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="muted">Current Pending:</span>
              <span className="mono" style={{ color: '#f87171' }}>₹{pending}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="muted">Receiving Now:</span>
              <span className="mono" style={{ color: '#34d399', fontWeight: 700 }}>
                {isValidAmount ? `− ₹${numAmount}` : '—'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #334155', paddingTop: 6, fontWeight: 700 }}>
              <span style={{ color: '#f8fafc' }}>Remaining Balance:</span>
              <span className="mono" style={{ color: remaining === 0 ? '#34d399' : '#fbbf24' }}>
                ₹{remaining}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-green"
              disabled={busy || !isValidAmount}
              style={{ minWidth: 160 }}
            >
              {busy ? 'Recording…' : `✓ Record ₹${isValidAmount ? numAmount : 0} Payment`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
